import React, { useState, useRef, useEffect } from 'react';
import XLogo from '../assets/OnlyX_white.png';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Typing Indicator ─────────────────────────────────────────────────────────
const TypingDots = () => (
  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '4px 0' }}>
    {[0, 1, 2].map(i => (
      <div key={i} style={{
        width: 9, height: 9, borderRadius: '50%',
        background: '#FC5C0B', opacity: 0.7,
        animation: 'cxBounce 1.2s infinite',
        animationDelay: `${i * 0.2}s`
      }} />
    ))}
    <style>{`
      @keyframes cxBounce {
        0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
        40% { transform: translateY(-6px); opacity: 1; }
      }
      @keyframes cxPulse {
        0%, 100% { box-shadow: 0 4px 16px rgba(15,31,69,0.4), 0 0 0 0 rgba(252,92,11,0.4); }
        50% { box-shadow: 0 4px 16px rgba(15,31,69,0.4), 0 0 0 8px rgba(252,92,11,0); }
      }
    `}</style>
  </div>
);

// ── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isDark }) => {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 10
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0f1f45, #1a3a7c)',
          border: '1.5px solid #FC5C0B',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginRight: 8, marginTop: 2, overflow: 'hidden'
        }}>
          <img src={XLogo} alt="X" style={{ width: 16, height: 16, objectFit: 'contain' }} />
        </div>
      )}
      <div style={{
        maxWidth: '78%',
        padding: '9px 13px',
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: isUser
          ? '#0f1f45'
          : isDark ? '#1e2d4a' : '#f4f6fb',
        color: isUser
          ? '#ffffff'
          : isDark ? '#e8edf5' : '#1a2340',
        fontSize: 13.5,
        lineHeight: 1.55,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        {msg.content}
      </div>
    </div>
  );
};

// ── Suggested Prompts ────────────────────────────────────────────────────────
const SUGGESTIONS = {
  crm: [
    'Which leads have been inactive for 30 days?',
    'Show me all leads in Proposal stage',
    'How many leads were added this month?',
    'List upcoming follow-ups this week',
    'Which leads are assigned to me?'
  ],
  finance: [
    'Who has the highest outstanding balance?',
    'What is total collection this month?',
    'Show me all overdue invoices',
    'List unpaid invoices above ₹10,000',
    'What is total outstanding amount?'
  ],
  tasks: [
    'Which tasks are overdue today?',
    'Show me all pending tasks',
    'How many tasks are due this week?',
    'Which tasks have highest priority?',
    'Show me recently added tasks'
  ],
  clients: [
    'How many active clients do I have?',
    'Show me clients added this month',
    'List all inactive clients',
    'How many clients are there in total?',
    'Show me clients from Uttar Pradesh'
  ],
  dashboard: [
    'Give me a business summary',
    'What needs my attention today?',
    'How is the firm performing this month?',
    'What are the top outstanding clients?',
    'Show me overdue tasks and invoices'
  ]
};

// ── Main Component ───────────────────────────────────────────────────────────
const MODULE_LABELS = {
  crm: 'CRM Assistant',
  finance: 'Finance Assistant',
  tasks: 'Tasks Assistant',
  clients: 'Clients Assistant',
  dashboard: 'Dashboard Assistant',
};

const ChanakyaXAI = ({ module = 'crm', isDark = false }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Drag state ──────────────────────────────────────────────────────────
  const [pos, setPos] = useState({ x: window.innerWidth - 82, y: window.innerHeight - 82 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);
  const prevModuleRef = useRef(module);

  const onMouseDown = (e) => {
    dragging.current = true;
    dragMoved.current = false;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    dragging.current = true;
    dragMoved.current = false;
    dragOffset.current = { x: t.clientX - pos.x, y: t.clientY - pos.y };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      dragMoved.current = true;
      const x = Math.min(Math.max(e.clientX - dragOffset.current.x, 10), window.innerWidth - 64);
      const y = Math.min(Math.max(e.clientY - dragOffset.current.y, 10), window.innerHeight - 64);
      setPos({ x, y });
    };
    const onTouchMove = (e) => {
      if (!dragging.current) return;
      dragMoved.current = true;
      const t = e.touches[0];
      const x = Math.min(Math.max(t.clientX - dragOffset.current.x, 10), window.innerWidth - 64);
      const y = Math.min(Math.max(t.clientY - dragOffset.current.y, 10), window.innerHeight - 64);
      setPos({ x, y });
    };
    const onMouseUp = () => { dragging.current = false; };
    const onTouchEnd = () => { dragging.current = false; };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [pos]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (!hasGreeted) {
        setMessages([{
          role: 'assistant',
          content: `Namaste! 🙏 I'm ChanakyaX AI — your intelligent CA firm assistant.\n\nI have live access to your ${MODULE_LABELS[module] || 'firm'} data. Ask me anything!`
        }]);
        setHasGreeted(true);
      }
    }
  }, [open]);

  // Reset greeting when module changes
  useEffect(() => {
    if (prevModuleRef.current !== module) {
      prevModuleRef.current = module;
      setHasGreeted(false);
      setMessages([{
        role: 'assistant',
        content: `Namaste! 🙏 I'm ChanakyaX AI — your intelligent CA firm assistant.\n\nI have live access to your ${MODULE_LABELS[module] || 'firm'} data. Ask me anything!`
      }]);
    }
  }, [module]);

  // ── Action Handler ───────────────────────────────────────────────────────
  const handleAction = async (actionType) => {
    if (actionType === 'payment-reminder-preview') {
      setActionLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE}/api/ai/action/payment-reminder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ sendEmails: false })
        });
        const data = await res.json();
        if (!data.clients?.length) {
          setMessages(prev => [...prev, { role: 'assistant', content: 'No outstanding clients found. All invoices are clear!' }]);
          setPendingAction(null);
          return;
        }
        const list = data.clients.map((c, i) =>
          `${i + 1}. ${c.name} — ₹${Number(c.outstanding).toLocaleString('en-IN', { minimumFractionDigits: 2 })} (${c.invoices} invoice${c.invoices > 1 ? 's' : ''})`
        ).join('\n');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I found ${data.clients.length} clients with outstanding dues:\n\n${list}\n\nShall I send payment reminder emails to all of them?`,
          action: { type: 'payment-reminder-confirm', clients: data.clients }
        }]);
        setPendingAction({ type: 'payment-reminder-confirm', clients: data.clients });
      } catch (err) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      } finally {
        setActionLoading(false);
      }
    }

    if (actionType === 'payment-reminder-send') {
      setActionLoading(true);
      setPendingAction(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE}/api/ai/action/payment-reminder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ sendEmails: true })
        });
        const data = await res.json();
        if (!res.ok) {
          setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${data.message}` }]);
          return;
        }
        const summary = data.results?.map(r =>
          `${r.status === 'sent' ? '✅' : r.status === 'skipped' ? '⚠️' : '❌'} ${r.name} — ${r.status}${r.reason ? ` (${r.reason})` : ''}`
        ).join('\n');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${data.message}\n\n${summary}`
        }]);
      } catch (err) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const sendMessage = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;

    // Detect payment reminder intent
    const reminderKeywords = ['payment reminder', 'send reminder', 'remind clients', 'outstanding reminder', 'overdue reminder'];
    if (reminderKeywords.some(k => userMsg.toLowerCase().includes(k))) {
      setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
      setInput('');
      handleAction('payment-reminder-preview');
      return;
    }

    setInput('');
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const history = newMessages
        .filter(m => !(m.role === 'assistant' && m.content.startsWith('Namaste')))
        .slice(-6)
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${BASE}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMsg,
          module,
          history: history.slice(0, -1)
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'AI error');

      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I ran into an issue: ${err.message}. Please try again.`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `Namaste! 🙏 I'm ChanakyaX AI — your intelligent CA firm assistant.\n\nI have live access to your CRM data. Ask me anything about your leads, follow-ups, or pipeline.`
    }]);
  };

  // ── Colors ───────────────────────────────────────────────────────────────
  const T = {
    bg: isDark ? '#0d1b35' : '#ffffff',
    border: isDark ? '#1e3a6e' : '#e2e8f0',
    inputBg: isDark ? '#1a2d50' : '#f8fafc',
    inputBorder: isDark ? '#2a4070' : '#d1d9e6',
    suggBg: isDark ? '#1a2d50' : '#f0f4ff',
    suggBorder: isDark ? '#2a4070' : '#c7d2fe',
    suggText: isDark ? '#93b4e0' : '#3730a3',
    text: isDark ? '#e2e8f0' : '#1e293b',
  };

  return (
    <>
      {/* ── Floating Button ── */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onClick={() => { if (!dragMoved.current) setOpen(o => !o); }}
        style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y,
          width: 54,
          height: 54,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0f1f45 0%, #1a3a7c 100%)',
          boxShadow: '0 4px 20px rgba(15,31,69,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 9999,
          border: '2.5px solid #FC5C0B',
          animation: !open ? 'cxPulse 2.5s infinite' : 'none',
          transition: 'transform 0.2s'
        }}
        title="ChanakyaX AI"
      >
        <img src={XLogo} alt="ChanakyaX AI" style={{ width: 55, height: 55, objectFit: 'contain' }} />
      </div>

      {/* ── Chat Panel ── */}
      {open && (
        <div style={{
          position: 'fixed',
          left: pos.x + 64 + 8 > window.innerWidth - 365
            ? pos.x - 365 - 8
            : pos.x + 64 + 8,
          top: pos.y + 530 > window.innerHeight
            ? pos.y - 530 + 54
            : pos.y,
          width: 365,
          height: 530,
          borderRadius: 18,
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: '0 12px 48px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9998,
          overflow: 'hidden'
        }}>

          {/* ── Header ── */}
          <div style={{
            background: '#ffffff',
            padding: '13px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              {/* Logo circle */}
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0f1f45, #1a3a7c)',
                border: '2px solid #FC5C0B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', flexShrink: 0
              }}>
                <img src={XLogo} alt="X" style={{ width: 22, height: 22, objectFit: 'contain' }} />
              </div>
              <div>
                <div style={{ color: '#0f1f45', fontWeight: 700, fontSize: 14.5, letterSpacing: 0.4 }}>
                  ChanakyaX AI
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ color: '#64748b', fontSize: 10.5, fontWeight: 500 }}>
                    {MODULE_LABELS[module] || 'AI Assistant'} · Live Data
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={clearChat}
              style={{
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: 7,
                color: '#64748b', fontSize: 11,
                padding: '4px 10px', cursor: 'pointer',
                transition: 'background 0.15s'
              }}
            >
              Clear
            </button>
          </div>

          {/* ── Messages ── */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '14px 12px 4px',
            display: 'flex', flexDirection: 'column'
          }}>
            {messages.map((msg, i) => (
              <div key={i}>
                <MessageBubble msg={msg} isDark={isDark} />
                {msg.action?.type === 'payment-reminder-confirm' && pendingAction && (
                  <div style={{ display: 'flex', gap: 8, marginLeft: 36, marginBottom: 10 }}>
                    <button
                      onClick={() => handleAction('payment-reminder-send')}
                      disabled={actionLoading}
                      style={{
                        background: 'linear-gradient(135deg,#16a34a,#15803d)',
                        border: 'none', borderRadius: 8, color: '#fff',
                        padding: '7px 16px', fontSize: 12, fontWeight: 600,
                        cursor: actionLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {actionLoading ? 'Sending...' : '✅ Yes, Send Reminders'}
                    </button>
                    <button
                      onClick={() => {
                        setPendingAction(null);
                        setMessages(prev => [...prev, { role: 'assistant', content: 'Cancelled. No emails sent.' }]);
                      }}
                      style={{
                        background: '#f1f5f9', border: '1px solid #e2e8f0',
                        borderRadius: 8, color: '#64748b',
                        padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0f1f45, #1a3a7c)',
                  border: '1.5px solid #FC5C0B',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, overflow: 'hidden'
                }}>
                  <img src={XLogo} alt="X" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                </div>
                <TypingDots />
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* ── Suggestions ── */}
          {messages.length === 1 && (
            <div style={{ padding: '4px 12px 8px' }}>
              <div style={{
                fontSize: 11, color: isDark ? '#4a6080' : '#94a3b8',
                marginBottom: 6, fontWeight: 500, letterSpacing: 0.3
              }}>
                QUICK QUESTIONS
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(SUGGESTIONS[module] || SUGGESTIONS.crm).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    style={{
                      background: T.suggBg,
                      border: `1px solid ${T.suggBorder}`,
                      borderRadius: 20,
                      padding: '5px 12px',
                      fontSize: 11.5,
                      color: T.suggText,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      textAlign: 'left'
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div style={{
                marginTop: 10,
                borderTop: `1px dashed ${isDark ? '#1e3a6e' : '#e2e8f0'}`,
                paddingTop: 8,
                fontSize: 11, color: isDark ? '#4a6080' : '#94a3b8',
                fontWeight: 500, letterSpacing: 0.3
              }}>
                OR TYPE YOUR OWN QUESTION ↓
              </div>
            </div>
          )}

          {/* ── Input ── */}
          <div style={{
            padding: '8px 12px 12px',
            borderTop: `1px solid ${T.border}`,
            display: 'flex', gap: 8, alignItems: 'flex-end'
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={`Ask ChanakyaX AI anything about ${MODULE_LABELS[module] || 'your firm'}...`}
              rows={1}
              style={{
                flex: 1,
                background: T.inputBg,
                border: `1px solid ${T.inputBorder}`,
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 13.5,
                color: T.text,
                resize: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                maxHeight: 80,
                overflowY: 'auto'
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36,
                borderRadius: 10,
                background: input.trim() && !loading
                  ? 'linear-gradient(135deg, #FC5C0B, #e04a00)'
                  : '#94a3b8',
                border: 'none',
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.2s',
                boxShadow: input.trim() && !loading ? '0 2px 8px rgba(252,92,11,0.4)' : 'none'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* ── Footer ── */}
          <div style={{
            textAlign: 'center', fontSize: 10.5,
            color: isDark ? '#2a3f60' : '#cbd5e1',
            paddingBottom: 8, letterSpacing: 0.3
          }}>
            ChanakyaX AI · Powered by Live DB
          </div>
        </div>
      )}
    </>
  );
};

export default ChanakyaXAI;